import { Holiday } from '../types';

// Hilfsfunktion für Oster-Berechnung
const calculateEaster = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
};

// Bewegliche Feiertage berechnen
const getMovableHolidays = (year: number): { [key: string]: Date } => {
  const easter = calculateEaster(year);
  
  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };
  
  return {
    'Karfreitag': addDays(easter, -2),
    'Ostermontag': addDays(easter, 1),
    'Auffahrt': addDays(easter, 39),
    'Pfingstmontag': addDays(easter, 50)
  };
};

const formatLocalDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// Feiertage für ein bestimmtes Jahr abrufen
export const getHolidaysForYear = (year: number): Holiday[] => {
  const fixedHolidays: Omit<Holiday, 'date'>[] = [
    {
      name: 'Neujahr',
      type: 'fixed',
      isNational: true,
      regions: ['ALL']
    },
    {
      name: 'Berchtoldstag',
      type: 'fixed',
      isNational: false,
      regions: ['BE', 'TG', 'VD', 'ZH']
    },
    {
      name: 'Tag der Arbeit',
      type: 'fixed',
      isNational: true,
      regions: ['ALL']
    },
    {
      name: 'Bundesfeiertag',
      type: 'fixed',
      isNational: true,
      regions: ['ALL']
    },
    {
      name: 'Weihnachten',
      type: 'fixed',
      isNational: true,
      regions: ['ALL']
    },
    {
      name: 'Stephanstag',
      type: 'fixed',
      isNational: true,
      regions: ['ALL']
    }
  ];

  const movableHolidayDates = getMovableHolidays(year);
  const fixedHolidayDates: Record<string, string> = {
    Neujahr: '01-01',
    Berchtoldstag: '01-02',
    'Tag der Arbeit': '05-01',
    Bundesfeiertag: '08-01',
    Weihnachten: '12-25',
    Stephanstag: '12-26',
  };
  const movableHolidayConfigs: Omit<Holiday, 'date'>[] = [
    {
      name: 'Karfreitag',
      type: 'movable',
      isNational: true,
      regions: ['ALL']
    },
    {
      name: 'Ostermontag',
      type: 'movable',
      isNational: true,
      regions: ['ALL']
    },
    {
      name: 'Auffahrt',
      type: 'movable',
      isNational: true,
      regions: ['ALL']
    },
    {
      name: 'Pfingstmontag',
      type: 'movable',
      isNational: true,
      regions: ['ALL']
    }
  ];

  const holidays: Holiday[] = [
    // Fixe Feiertage
    ...fixedHolidays.map(holiday => ({
      ...holiday,
      date: `${year}-${fixedHolidayDates[holiday.name]}`
    })),
    // Bewegliche Feiertage
    ...movableHolidayConfigs.map(holiday => ({
      ...holiday,
      date: formatLocalDate(movableHolidayDates[holiday.name])
    }))
  ];

  return holidays;
};

// Region aus Postleitzahl ermitteln
export const getRegionFromPostalCode = (postalCode: string): string => {
  // Mapping von PLZ zu Kantonen
  const plzMapping: { [key: string]: string } = {
    '1': 'VD',
    '2': 'NE',
    '3': 'BE',
    '4': 'BS',
    '5': 'AG',
    '6': 'LU',
    '7': 'GR',
    '8': 'ZH',
    '9': 'SG'
  };

  const firstDigit = postalCode.charAt(0);
  return plzMapping[firstDigit] || 'ZH'; // Default zu Zürich wenn unbekannt
};

// Prüfen ob ein Datum ein Feiertag ist
export const isHoliday = (date: Date, region: string): boolean => {
  const holidays = getHolidaysForYear(date.getFullYear());
  const dateString = date.toISOString().split('T')[0];

  return holidays.some(holiday => 
    holiday.date === dateString && 
    (holiday.regions.includes('ALL') || holiday.regions.includes(region))
  );
};

// Namen des Feiertags abrufen
export const getHolidayName = (date: Date, region: string): string | null => {
  const holidays = getHolidaysForYear(date.getFullYear());
  const dateString = date.toISOString().split('T')[0];

  const holiday = holidays.find(holiday => 
    holiday.date === dateString && 
    (holiday.regions.includes('ALL') || holiday.regions.includes(region))
  );

  return holiday ? holiday.name : null;
}; 
